/**
 * GitHub App Setup Page - Platform Identity Management
 *
 * Features:
 * - Display current GitHub App installation status
 * - "Connect GitHub" button that redirects to GitHub App installation URL
 * - List of connected repositories (from installation)
 * - Disconnect/uninstall option
 * - Configuration for auto-approval settings (none, docs_only, tests_and_docs, non_source, full_trust)
 * - Approval group selection dropdown
 *
 * API Endpoints:
 * - GET /api/github-app/installations - List client's installations
 * - DELETE /api/github-app/installations/:id - Disconnect installation
 *
 * Access: Authenticated users with company context
 * Standards: .equilateral-standards/serverless-saas-aws/api_standards.md
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Card,
  Badge,
  Spinner,
  Alert,
  Modal,
  Label,
  Select,
  Table,
  Tooltip,
} from 'flowbite-react';
import {
  HiExternalLink,
  HiRefresh,
  HiCheck,
  HiExclamation,
  HiCog,
  HiTrash,
  HiLink,
  HiInformationCircle,
} from 'react-icons/hi';
import { FaGithub } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { Make_Authorized_API_Call, isSuccessResponse } from '../../api/apiClient';
import { API_ENDPOINTS } from '../../services/api/endpoints';

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

/**
 * GitHub App installation record from backend
 */
interface GitHubInstallation {
  Installation_ID: string;
  Client_ID: string;
  GitHub_Account_Login: string;
  GitHub_Account_Type: 'User' | 'Organization';
  GitHub_Installation_ID: number;
  Installation_Status: 'active' | 'suspended' | 'pending' | 'disconnected';
  Auto_Approval_Level: AutoApprovalLevel;
  Approval_Group_ID: string | null;
  Repository_Count: number;
  Created_At: string;
  Updated_At: string;
}

/**
 * Repository connected via GitHub App installation
 */
interface ConnectedRepository {
  Repository_ID: string;
  Installation_ID: string;
  GitHub_Repo_ID: number;
  Repository_Name: string;
  Repository_Full_Name: string;
  Private: boolean;
  Default_Branch: string;
  Added_At: string;
}

/**
 * Approval group for commit approvals
 */
interface ApprovalGroup {
  Approval_Group_ID: string;
  Group_Name: string;
  Description: string;
  Member_Count: number;
}

/**
 * Auto-approval level for platform commits
 * - none: All commits require manual approval
 * - docs_only: Auto-approve documentation changes only
 * - tests_and_docs: Auto-approve tests and documentation
 * - non_source: Auto-approve non-source files (configs, docs, tests)
 * - full_trust: Auto-approve all commits (use with caution)
 */
type AutoApprovalLevel = 'none' | 'docs_only' | 'tests_and_docs' | 'non_source' | 'full_trust';

const AUTO_APPROVAL_OPTIONS: { value: AutoApprovalLevel; label: string; description: string }[] = [
  {
    value: 'none',
    label: 'None (Manual Approval Required)',
    description: 'All platform commits require manual approval by an authorized approver.',
  },
  {
    value: 'docs_only',
    label: 'Documentation Only',
    description: 'Auto-approve changes to README, docs/, and other documentation files.',
  },
  {
    value: 'tests_and_docs',
    label: 'Tests and Documentation',
    description: 'Auto-approve changes to test files and documentation.',
  },
  {
    value: 'non_source',
    label: 'Non-Source Files',
    description: 'Auto-approve changes that do not affect source code (configs, docs, tests).',
  },
  {
    value: 'full_trust',
    label: 'Full Trust (Auto-Approve All)',
    description: 'Auto-approve all platform commits. Use with caution.',
  },
];

// =============================================================================
// COMPONENT
// =============================================================================

const GitHubAppSetupPage: React.FC = () => {
  // Auth context for client/company context
  const { selectedClientId, selectedCompanyId } = useAuth();

  // Installation state
  const [installations, setInstallations] = useState<GitHubInstallation[]>([]);
  const [selectedInstallation, setSelectedInstallation] = useState<GitHubInstallation | null>(null);
  const [repositories, setRepositories] = useState<ConnectedRepository[]>([]);
  const [approvalGroups, setApprovalGroups] = useState<ApprovalGroup[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Modal state
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [saving, setSaving] = useState(false);

  // Settings form state
  const [settingsForm, setSettingsForm] = useState<{
    autoApprovalLevel: AutoApprovalLevel;
    approvalGroupId: string | null;
  }>({
    autoApprovalLevel: 'none',
    approvalGroupId: null,
  });

  // GitHub App installation URL - configured via environment
  const GITHUB_APP_INSTALL_URL =
    process.env.REACT_APP_GITHUB_APP_INSTALL_URL ||
    'https://github.com/apps/equilateral-agents/installations/new';

  // =============================================================================
  // DATA FETCHING
  // =============================================================================

  /**
   * Fetch GitHub App installations for the current client
   */
  const fetchInstallations = useCallback(async () => {
    if (!selectedClientId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log('[GitHubAppSetupPage] Fetching installations for client:', selectedClientId);

      const response = await Make_Authorized_API_Call<GitHubInstallation>(
        API_ENDPOINTS.GITHUB_APP.INSTALLATIONS_LIST,
        'GET',
        undefined,
        { params: { Client_ID: selectedClientId } }
      );

      if (isSuccessResponse(response)) {
        const fetchedInstallations = response.data.Records || [];
        setInstallations(fetchedInstallations);
        console.log('[GitHubAppSetupPage] Fetched installations:', fetchedInstallations.length);

        // If there's exactly one installation, auto-select it
        if (fetchedInstallations.length === 1) {
          setSelectedInstallation(fetchedInstallations[0]);
          await fetchRepositories(fetchedInstallations[0].Installation_ID);
        }
      } else {
        // No installations yet - not an error
        setInstallations([]);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch GitHub installations';
      setError(errorMessage);
      console.error('[GitHubAppSetupPage] Error fetching installations:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedClientId]);

  /**
   * Fetch repositories for a specific installation
   */
  const fetchRepositories = async (installationId: string) => {
    try {
      setLoadingRepos(true);

      console.log('[GitHubAppSetupPage] Fetching repositories for installation:', installationId);

      const response = await Make_Authorized_API_Call<ConnectedRepository>(
        API_ENDPOINTS.GITHUB_APP.INSTALLATION_REPOS,
        'GET',
        undefined,
        { params: { installation_id: installationId } }
      );

      if (isSuccessResponse(response)) {
        setRepositories(response.data.Records || []);
        console.log('[GitHubAppSetupPage] Fetched repositories:', response.data.Records?.length || 0);
      } else {
        // Non-critical - just log it
        console.warn('[GitHubAppSetupPage] Failed to fetch repositories:', response.message);
        setRepositories([]);
      }
    } catch (err) {
      console.error('[GitHubAppSetupPage] Error fetching repositories:', err);
      setRepositories([]);
    } finally {
      setLoadingRepos(false);
    }
  };

  /**
   * Fetch approval groups for the dropdown
   */
  const fetchApprovalGroups = useCallback(async () => {
    if (!selectedClientId) return;

    try {
      const response = await Make_Authorized_API_Call<ApprovalGroup>(
        API_ENDPOINTS.APPROVAL_GROUPS.LIST,
        'GET',
        undefined,
        { params: { Client_ID: selectedClientId } }
      );

      if (isSuccessResponse(response)) {
        setApprovalGroups(response.data.Records || []);
      }
    } catch (err) {
      console.error('[GitHubAppSetupPage] Error fetching approval groups:', err);
      // Non-critical, don't show error
    }
  }, [selectedClientId]);

  // Initial data fetch
  useEffect(() => {
    fetchInstallations();
    fetchApprovalGroups();
  }, [fetchInstallations, fetchApprovalGroups]);

  // =============================================================================
  // EVENT HANDLERS
  // =============================================================================

  /**
   * Handle connecting GitHub - redirects to GitHub App installation
   */
  const handleConnectGitHub = () => {
    console.log('[GitHubAppSetupPage] Redirecting to GitHub App installation');

    // Include state parameter for post-install redirect
    const stateParam = encodeURIComponent(
      JSON.stringify({
        client_id: selectedClientId,
        company_id: selectedCompanyId,
        return_url: window.location.href,
      })
    );

    const installUrl = `${GITHUB_APP_INSTALL_URL}?state=${stateParam}`;
    window.location.href = installUrl;
  };

  /**
   * Handle selecting an installation to view details
   */
  const handleSelectInstallation = async (installation: GitHubInstallation) => {
    setSelectedInstallation(installation);
    await fetchRepositories(installation.Installation_ID);
  };

  /**
   * Handle opening settings modal
   */
  const handleOpenSettings = (installation: GitHubInstallation) => {
    setSelectedInstallation(installation);
    setSettingsForm({
      autoApprovalLevel: installation.Auto_Approval_Level || 'none',
      approvalGroupId: installation.Approval_Group_ID,
    });
    setShowSettingsModal(true);
  };

  /**
   * Handle saving installation settings
   */
  const handleSaveSettings = async () => {
    if (!selectedInstallation) return;

    try {
      setSaving(true);
      setError(null);

      console.log(
        '[GitHubAppSetupPage] Saving settings for installation:',
        selectedInstallation.Installation_ID
      );

      const response = await Make_Authorized_API_Call(
        API_ENDPOINTS.GITHUB_APP.INSTALLATIONS_LIST,
        'PUT',
        {
          Installation_ID: selectedInstallation.Installation_ID,
          Auto_Approval_Level: settingsForm.autoApprovalLevel,
          Approval_Group_ID: settingsForm.approvalGroupId,
        }
      );

      if (isSuccessResponse(response)) {
        setSuccessMessage('Settings saved successfully');
        setShowSettingsModal(false);

        // Update local state
        setInstallations((prev) =>
          prev.map((inst) =>
            inst.Installation_ID === selectedInstallation.Installation_ID
              ? {
                  ...inst,
                  Auto_Approval_Level: settingsForm.autoApprovalLevel,
                  Approval_Group_ID: settingsForm.approvalGroupId,
                }
              : inst
          )
        );

        // Update selected installation
        setSelectedInstallation((prev) =>
          prev
            ? {
                ...prev,
                Auto_Approval_Level: settingsForm.autoApprovalLevel,
                Approval_Group_ID: settingsForm.approvalGroupId,
              }
            : null
        );

        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        throw new Error(response.message || 'Failed to save settings');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save settings';
      setError(errorMessage);
      console.error('[GitHubAppSetupPage] Error saving settings:', err);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Handle disconnecting an installation
   */
  const handleDisconnect = async () => {
    if (!selectedInstallation) return;

    try {
      setDisconnecting(true);
      setError(null);

      console.log(
        '[GitHubAppSetupPage] Disconnecting installation:',
        selectedInstallation.Installation_ID
      );

      // Per API standards: DELETE with body containing ID
      const response = await Make_Authorized_API_Call(
        API_ENDPOINTS.GITHUB_APP.INSTALLATIONS_LIST,
        'DELETE',
        {
          Installation_ID: selectedInstallation.Installation_ID,
        }
      );

      if (isSuccessResponse(response)) {
        setSuccessMessage('GitHub App disconnected successfully');
        setShowDisconnectModal(false);
        setSelectedInstallation(null);
        setRepositories([]);

        // Remove from local state
        setInstallations((prev) =>
          prev.filter((inst) => inst.Installation_ID !== selectedInstallation.Installation_ID)
        );

        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        throw new Error(response.message || 'Failed to disconnect GitHub App');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to disconnect GitHub App';
      setError(errorMessage);
      console.error('[GitHubAppSetupPage] Error disconnecting:', err);
    } finally {
      setDisconnecting(false);
    }
  };

  // =============================================================================
  // RENDER HELPERS
  // =============================================================================

  /**
   * Render installation status badge
   */
  const renderStatusBadge = (status: GitHubInstallation['Installation_Status']) => {
    const statusConfig = {
      active: { color: 'success', icon: HiCheck },
      suspended: { color: 'warning', icon: HiExclamation },
      pending: { color: 'info', icon: HiInformationCircle },
      disconnected: { color: 'failure', icon: HiExclamation },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge color={config.color as 'success' | 'warning' | 'info' | 'failure'} className="inline-flex items-center gap-1">
        <Icon className="w-3 h-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  /**
   * Render auto-approval level badge
   */
  const renderAutoApprovalBadge = (level: AutoApprovalLevel) => {
    const levelConfig = {
      none: { color: 'gray', label: 'Manual Approval' },
      docs_only: { color: 'info', label: 'Docs Only' },
      tests_and_docs: { color: 'info', label: 'Tests & Docs' },
      non_source: { color: 'warning', label: 'Non-Source' },
      full_trust: { color: 'failure', label: 'Full Trust' },
    };

    const config = levelConfig[level] || levelConfig.none;

    return (
      <Badge color={config.color as 'gray' | 'info' | 'warning' | 'failure'} size="sm">
        {config.label}
      </Badge>
    );
  };

  // =============================================================================
  // RENDER: NO CLIENT CONTEXT
  // =============================================================================

  if (!selectedClientId) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">GitHub App Setup</h2>
          <p className="text-gray-600">Connect your GitHub account to enable platform identity features</p>
        </div>
        <Alert color="warning">
          <span className="font-medium">No client selected.</span> Please select a client from the
          dashboard to configure GitHub App settings.
        </Alert>
      </div>
    );
  }

  // =============================================================================
  // RENDER: LOADING STATE
  // =============================================================================

  if (loading) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">GitHub App Setup</h2>
          <p className="text-gray-600">Connect your GitHub account to enable platform identity features</p>
        </div>
        <div className="flex items-center justify-center py-12">
          <Spinner size="xl" />
          <span className="ml-3 text-gray-600">Loading GitHub installations...</span>
        </div>
      </div>
    );
  }

  // =============================================================================
  // RENDER: MAIN CONTENT
  // =============================================================================

  return (
    <div className="p-6">
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">GitHub App Setup</h2>
            <p className="text-gray-600">
              Connect your GitHub account to enable platform identity features for repository operations
            </p>
          </div>
          <div className="flex gap-2">
            <Button color="gray" onClick={fetchInstallations} aria-label="Refresh installations">
              <HiRefresh className="w-5 h-5 mr-2" />
              Refresh
            </Button>
            <Button color="blue" onClick={handleConnectGitHub} aria-label="Connect new GitHub account">
              <HiLink className="w-5 h-5 mr-2" />
              Connect GitHub
            </Button>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <Alert color="failure" className="mb-4" onDismiss={() => setError(null)}>
          <span className="font-medium">Error:</span> {error}
        </Alert>
      )}

      {successMessage && (
        <Alert color="success" className="mb-4" onDismiss={() => setSuccessMessage(null)}>
          <span className="font-medium">Success:</span> {successMessage}
        </Alert>
      )}

      {/* No Installations State */}
      {installations.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <FaGithub className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No GitHub App Connected</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Connect the Equilateral Agents GitHub App to enable platform identity features. This
              allows secure repository access for code generation, PR creation, and automated commits.
            </p>
            <Button color="blue" onClick={handleConnectGitHub}>
              <FaGithub className="w-5 h-5 mr-2" />
              Connect GitHub App
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Installations List */}
          <div className="lg:col-span-1">
            <Card>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Connected Accounts</h3>
              <div className="space-y-3">
                {installations.map((installation) => (
                  <div
                    key={installation.Installation_ID}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedInstallation?.Installation_ID === installation.Installation_ID
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    onClick={() => handleSelectInstallation(installation)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSelectInstallation(installation)}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900">
                        {installation.GitHub_Account_Login}
                      </span>
                      {renderStatusBadge(installation.Installation_Status)}
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">
                        {installation.GitHub_Account_Type} - {installation.Repository_Count} repos
                      </span>
                      {renderAutoApprovalBadge(installation.Auto_Approval_Level || 'none')}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Installation Details */}
          <div className="lg:col-span-2">
            {selectedInstallation ? (
              <Card>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {selectedInstallation.GitHub_Account_Login}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {selectedInstallation.GitHub_Account_Type} account - Connected{' '}
                      {new Date(selectedInstallation.Created_At).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Tooltip content="Configure auto-approval and approval groups">
                      <Button
                        color="gray"
                        size="sm"
                        onClick={() => handleOpenSettings(selectedInstallation)}
                        aria-label="Configure settings"
                      >
                        <HiCog className="w-4 h-4 mr-1" />
                        Settings
                      </Button>
                    </Tooltip>
                    <Tooltip content="Disconnect this GitHub account">
                      <Button
                        color="failure"
                        size="sm"
                        onClick={() => setShowDisconnectModal(true)}
                        aria-label="Disconnect GitHub account"
                      >
                        <HiTrash className="w-4 h-4 mr-1" />
                        Disconnect
                      </Button>
                    </Tooltip>
                  </div>
                </div>

                {/* Status and Settings Summary */}
                <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <span className="text-sm text-gray-500">Status</span>
                    <div className="mt-1">
                      {renderStatusBadge(selectedInstallation.Installation_Status)}
                    </div>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Auto-Approval Level</span>
                    <div className="mt-1">
                      {renderAutoApprovalBadge(selectedInstallation.Auto_Approval_Level || 'none')}
                    </div>
                  </div>
                </div>

                {/* Connected Repositories */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Connected Repositories</h4>
                  {loadingRepos ? (
                    <div className="flex items-center justify-center py-8">
                      <Spinner size="md" />
                      <span className="ml-2 text-gray-500">Loading repositories...</span>
                    </div>
                  ) : repositories.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>No repositories connected to this installation.</p>
                      <a
                        href={`https://github.com/settings/installations/${selectedInstallation.GitHub_Installation_ID}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline inline-flex items-center mt-2"
                      >
                        Configure repository access on GitHub
                        <HiExternalLink className="w-4 h-4 ml-1" />
                      </a>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table hoverable>
                        <Table.Head>
                          <Table.HeadCell>Repository</Table.HeadCell>
                          <Table.HeadCell>Visibility</Table.HeadCell>
                          <Table.HeadCell>Default Branch</Table.HeadCell>
                          <Table.HeadCell>Added</Table.HeadCell>
                        </Table.Head>
                        <Table.Body className="divide-y">
                          {repositories.map((repo) => (
                            <Table.Row key={repo.Repository_ID} className="bg-white">
                              <Table.Cell className="font-medium text-gray-900">
                                <a
                                  href={`https://github.com/${repo.Repository_Full_Name}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="hover:text-blue-600 hover:underline inline-flex items-center"
                                >
                                  {repo.Repository_Full_Name}
                                  <HiExternalLink className="w-3 h-3 ml-1" />
                                </a>
                              </Table.Cell>
                              <Table.Cell>
                                <Badge color={repo.Private ? 'gray' : 'success'} size="sm">
                                  {repo.Private ? 'Private' : 'Public'}
                                </Badge>
                              </Table.Cell>
                              <Table.Cell>
                                <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                                  {repo.Default_Branch}
                                </code>
                              </Table.Cell>
                              <Table.Cell className="text-gray-500">
                                {new Date(repo.Added_At).toLocaleDateString()}
                              </Table.Cell>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table>
                    </div>
                  )}
                </div>

                {/* Manage on GitHub Link */}
                <div className="mt-4 pt-4 border-t">
                  <a
                    href={`https://github.com/settings/installations/${selectedInstallation.GitHub_Installation_ID}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline inline-flex items-center text-sm"
                  >
                    Manage installation on GitHub
                    <HiExternalLink className="w-4 h-4 ml-1" />
                  </a>
                </div>
              </Card>
            ) : (
              <Card>
                <div className="text-center py-12 text-gray-500">
                  <HiInformationCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Select an installation from the list to view details</p>
                </div>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Settings Modal */}
      <Modal show={showSettingsModal} onClose={() => setShowSettingsModal(false)} size="lg">
        <Modal.Header>Installation Settings</Modal.Header>
        <Modal.Body>
          <div className="space-y-6">
            {/* Auto-Approval Level */}
            <div>
              <Label htmlFor="autoApprovalLevel" value="Auto-Approval Level" className="mb-2 block" />
              <Select
                id="autoApprovalLevel"
                value={settingsForm.autoApprovalLevel}
                onChange={(e) =>
                  setSettingsForm((prev) => ({
                    ...prev,
                    autoApprovalLevel: e.target.value as AutoApprovalLevel,
                  }))
                }
              >
                {AUTO_APPROVAL_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
              <p className="mt-2 text-sm text-gray-500">
                {
                  AUTO_APPROVAL_OPTIONS.find((o) => o.value === settingsForm.autoApprovalLevel)
                    ?.description
                }
              </p>
              {settingsForm.autoApprovalLevel === 'full_trust' && (
                <Alert color="warning" className="mt-3">
                  <span className="font-medium">Warning:</span> Full trust mode auto-approves all
                  platform commits without human review. Only enable this for development environments
                  or low-risk repositories.
                </Alert>
              )}
            </div>

            {/* Approval Group Selection */}
            <div>
              <Label htmlFor="approvalGroup" value="Approval Group" className="mb-2 block" />
              <Select
                id="approvalGroup"
                value={settingsForm.approvalGroupId || ''}
                onChange={(e) =>
                  setSettingsForm((prev) => ({
                    ...prev,
                    approvalGroupId: e.target.value || null,
                  }))
                }
              >
                <option value="">No approval group (default approvers)</option>
                {approvalGroups.map((group) => (
                  <option key={group.Approval_Group_ID} value={group.Approval_Group_ID}>
                    {group.Group_Name} ({group.Member_Count} members)
                  </option>
                ))}
              </Select>
              <p className="mt-2 text-sm text-gray-500">
                Select an approval group to route platform commit approvals. If not set, default
                client approvers will be used.
              </p>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex justify-end gap-2">
            <Button color="gray" onClick={() => setShowSettingsModal(false)}>
              Cancel
            </Button>
            <Button color="blue" onClick={handleSaveSettings} disabled={saving}>
              {saving ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Saving...
                </>
              ) : (
                'Save Settings'
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Disconnect Confirmation Modal */}
      <Modal show={showDisconnectModal} onClose={() => setShowDisconnectModal(false)} size="md">
        <Modal.Header>Disconnect GitHub App</Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <HiExclamation className="mx-auto mb-4 h-14 w-14 text-red-600" />
            <h3 className="mb-5 text-lg font-normal text-gray-500">
              Are you sure you want to disconnect{' '}
              <span className="font-semibold">{selectedInstallation?.GitHub_Account_Login}</span>?
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              This will revoke access to all connected repositories. Platform features that depend
              on repository access will stop working. You can reconnect at any time.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex justify-center gap-4">
            <Button color="gray" onClick={() => setShowDisconnectModal(false)}>
              Cancel
            </Button>
            <Button color="failure" onClick={handleDisconnect} disabled={disconnecting}>
              {disconnecting ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Disconnecting...
                </>
              ) : (
                "Yes, I'm sure"
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GitHubAppSetupPage;
